import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateGoodsReceiptDTO,
  GoodsReceiptListQueryDTO,
  ReorderPlanQueryDTO,
  UpdateGoodsReceiptDTO,
} from "@/modules/inventory/domain/contracts";
import { inventoryApi } from "./client";

/** Goods receipt, planning, bundle assembly — Phase 13 import orders ໃຊ້ `./import-orders-queries` */

const keys = {
  all: ["inventory"] as const,
  receipts: (query?: GoodsReceiptListQueryDTO) =>
    [...keys.all, "receipts", query] as const,
  detail: (id: string) => [...keys.all, "detail", id] as const,
  inTransit: () => [...keys.all, "in-transit"] as const,
  reorderHints: (limit?: number) =>
    [...keys.all, "reorder-hints", limit] as const,
  reorderPlan: (query?: ReorderPlanQueryDTO) =>
    [...keys.all, "reorder-plan", query] as const,
  stockWithoutLot: (limit?: number) =>
    [...keys.all, "stock-without-lot", limit] as const,
  variantLots: (variantId: string, includeDepleted?: boolean) =>
    [...keys.all, "variant-lots", variantId, includeDepleted] as const,
};

export function useGoodsReceiptsQuery(query?: GoodsReceiptListQueryDTO) {
  return useQuery({
    queryKey: keys.receipts(query),
    queryFn: () => inventoryApi.listReceipts(query),
  });
}

export function useGoodsReceiptQuery(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => inventoryApi.getReceipt(id),
    enabled: Boolean(id),
  });
}

export function useCreateGoodsReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGoodsReceiptDTO) =>
      inventoryApi.createReceipt(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useUpdateGoodsReceipt(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateGoodsReceiptDTO) =>
      inventoryApi.updateReceipt(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useConfirmGoodsReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inventoryApi.confirmReceipt(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      // สต็อกเปลี่ยน → refresh products
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useDuplicateGoodsReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inventoryApi.duplicateReceipt(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.setQueryData(keys.detail(data.id), data);
    },
  });
}

export function useVoidGoodsReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inventoryApi.voidReceipt(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useDeleteGoodsReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inventoryApi.deleteReceipt(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useInTransitSummaryQuery() {
  return useQuery({
    queryKey: keys.inTransit(),
    queryFn: () => inventoryApi.getInTransitSummary(),
  });
}

export function useReorderHintsQuery(limit = 20) {
  return useQuery({
    queryKey: keys.reorderHints(limit),
    queryFn: () => inventoryApi.getReorderHints(limit),
  });
}

export function useReorderPlanQuery(query: ReorderPlanQueryDTO) {
  return useQuery({
    queryKey: keys.reorderPlan(query),
    queryFn: () => inventoryApi.getReorderPlan(query),
  });
}

export function useStockWithoutLotQuery(limit = 20) {
  return useQuery({
    queryKey: keys.stockWithoutLot(limit),
    queryFn: () => inventoryApi.getStockWithoutLotSummary(limit),
  });
}

export function useVariantStockLotsQuery(
  variantId: string,
  includeDepleted = false,
) {
  return useQuery({
    queryKey: keys.variantLots(variantId, includeDepleted),
    queryFn: () => inventoryApi.getVariantStockLots(variantId, includeDepleted),
    enabled: Boolean(variantId),
  });
}

export function useBundleAssemblableQty(bundleVariantId: string) {
  return useQuery({
    queryKey: [...keys.all, "bundle-assemblable", bundleVariantId] as const,
    queryFn: () => inventoryApi.getBundleAssemblableQty(bundleVariantId),
    enabled: Boolean(bundleVariantId),
  });
}

export function useBundleAssemblyPreview(
  bundleVariantId: string,
  quantity?: number,
) {
  return useQuery({
    queryKey: [
      ...keys.all,
      "bundle-assembly-preview",
      bundleVariantId,
      quantity,
    ] as const,
    queryFn: () =>
      inventoryApi.previewBundleAssembly({
        bundleVariantId,
        quantity: quantity ?? 1,
      }),
    enabled: Boolean(bundleVariantId) && (quantity ?? 0) > 0,
  });
}

export function useAssembleBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inventoryApi.assembleBundle,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useBundleDisassemblyPreview(
  bundleVariantId: string,
  quantity?: number,
) {
  return useQuery({
    queryKey: [
      ...keys.all,
      "bundle-disassembly-preview",
      bundleVariantId,
      quantity,
    ] as const,
    queryFn: () =>
      inventoryApi.previewBundleDisassembly({
        bundleVariantId,
        quantity: quantity ?? 1,
      }),
    enabled: Boolean(bundleVariantId) && (quantity ?? 0) > 0,
  });
}

export function useDisassembleBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inventoryApi.disassembleBundle,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}
